// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * A control component for displaying and editing asset locations.
 * This module provides a clickable interface for viewing and
 * modifying an asset's current location, with support for
 * both stationary locations and in-transit states.
 *
 * Key features:
 * - Location display
 * - Transit state handling
 * - Address display
 * - Tooltip information
 * - Click interaction
 */
import { Asset } from '@/asset/api/types';
import { EditablePropertyButton } from '@/components/editable-property-button';
import {
  getEffectiveAddress,
  getEffectiveLocationName,
} from '@/location/utilities/get-effective-third-party';
import { Stack, Tooltip, Typography } from '@mui/material';

/**
 * Props for configuring the AssetLocationControl component.
 *
 * @interface Props
 */
interface Props {
  /**
   * The asset whose location is being displayed.
   * Contains current location and transit state.
   */
  asset: Asset;

  /**
   * Callback fired when the control is clicked.
   * Typically opens a dialog for location editing.
   */
  onClick: () => void;
}

/**
 * A control component for asset location display and editing.
 * Shows the current location name and address, or an in-transit
 * state, with a tooltip for additional details. Clicking opens
 * an edit interface.
 *
 * Features:
 * - Location name display
 * - Address subtext
 * - Transit state handling
 * - Tooltip with details
 * - Click-to-edit behavior
 *
 * Usage example:
 * ```tsx
 * function AssetDetails({ asset }: Props) {
 *   const handleLocationClick = () => {
 *     // Open location edit dialog
 *   };
 *
 *   return (
 *     <AssetLocationControl
 *       asset={asset}
 *       onClick={handleLocationClick}
 *     />
 *   );
 * }
 * ```
 */
export function AssetLocationControl(props: Props) {
  const loc = props.asset.currentLocation;

  const displayName = loc ? getEffectiveLocationName(loc) : undefined;
  const displayAddress = loc ? getEffectiveAddress(loc) : null;

  // Prepare tooltip content based on transit state
  const locationTooltip = props.asset.isInTransit ? (
    'In transit'
  ) : (
    <>
      <b>{displayName}</b>
      <br />
      <em>{displayAddress}</em>
    </>
  );

  return (
    <EditablePropertyButton onClick={props.onClick}>
      <Tooltip arrow placement="top" title={locationTooltip}>
        <Stack alignItems="start" minWidth="5rem" maxWidth="10rem">
          <Typography variant="body2" maxWidth="100%" noWrap>
            {props.asset.isInTransit ? 'In transit' : displayName}
          </Typography>
          {loc && (
            <Typography
              variant="body2"
              color="text.secondary"
              maxWidth="100%"
              noWrap
            >
              {displayAddress}
            </Typography>
          )}
        </Stack>
      </Tooltip>
    </EditablePropertyButton>
  );
}
